import {
  GlobalIngredientsDTO,
  LiquidClassDTO,
  PFDocAttachmentDTO,
  PFDocDataDTO,
  PFDocProteinFormulationDTO,
  PFDocProteinFormulationIngredientsDTO,
  PFDocSaveProteinFormulationDTO,
  PFDocSaveResult,
  PFDocStandardIngredientsDTO,
  PFStateDTO,
  ProteinFormulationIngredientTypeDTO,
} from 'business/models/Models';
import RecentDocumentService from 'business/services/RecentDocumentService';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  isNullOrUndefined,
  showSnackBar,
  showAlertSnackBar,
  SetGlobalExpData,
} from 'utils/Global';
import './AddProtein.scss';
import StandardIngredients from './StandardIngredients';
import ProteinIngredients from './ProteinIngredients';
import RMDatePicker from 'components/customComponents/Controls/RMDatePicker';
import useModel from 'business/models/useModel';
import ProteinFormulationService from 'business/services/ProteinFormulationService';
import { MIME_TYPES } from 'utils/MimeTypes';
import cloneDeep from 'lodash/cloneDeep';
import { RMInput } from './RMInput';
import Header from 'components/header/Header';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import useBroadcast, { CHANNEL_ACTIONS, CHANNEL_TYPES } from 'business/broadcast';
import { TreeService } from 'business/services/TreeService';
import { RMModal, LoadingModal } from 'components/modal';
import RMCustomButton from 'components/customComponents/CommonComponents/RMCustomButton/RMCustomButton';
import {
  actionButtonsEnable,
  actionButtonStyle,
  iconSize,
} from './ProteinVariant/Constants';
import { KeyboardKeys } from 'constants/types';
import { useFileNotFoundModal } from 'CustomHooks/useFileNotFoundModal';
import ETreeNodeAccessRightNames from 'enums/ETreeNodeAccessRightNames';
import { useSaveDiscardButtons } from 'CustomHooks/useSaveDiscardButtons';

let deletedProteinIngredients: PFDocProteinFormulationIngredientsDTO[] = [];
let deletedStandardIngredients: PFDocStandardIngredientsDTO[] = [];
let deletedPFAttachments: PFDocAttachmentDTO[] = [];
let unChangedProteinFormulation: PFDocProteinFormulationDTO =
  new PFDocProteinFormulationDTO();

const AddProtein = (props) => {
  const [datePrepared, setDatePrepared] = useState<Date>(new Date());
  const [proteinFormulation, setProteinFormulation] =
    useState<PFDocProteinFormulationDTO>(new PFDocProteinFormulationDTO());
  const treeNodeId = props ? props.match.params.nodeId : -1;
  const [pfName, setPFName] = useState('');
  const [treeNode, setTreeNode] = useState(null);
  const [pfIngTypes, setPfIngTypes] = useState<ProteinFormulationIngredientTypeDTO[]>([]);
  const [liquidClasses, setLiquidClasses] = useState<LiquidClassDTO[]>([]);
  const [loading, setLoading] = useState<{ show: boolean; text: string }>({
    show: true,
    text: 'Loading',
  });
  const [accessRightName, setAccessRightName] = useState<string>('');
  const [saveDisabled, setSaveDisabled] = useState<boolean>(true);
  const [showError, setShowError] = useState<boolean>(false);
  const [pfErrors, setPFError] = useState<string>('');
  const inputFileRef = useRef(null);
  const [globalIngredientsData, setGlobalIngredientsData] = useState(null);
  const explorerBroadcastChannel = useBroadcast(CHANNEL_TYPES.EXPLORER);
  const globalDataBroadcastChannel = useBroadcast(CHANNEL_TYPES.GLOBAL_DATA);
  const [getProteinFormulationDocData] = useModel(
    ProteinFormulationService,
    'getProteinFormulationDocData',
    [],
  );
  const [getGlobalIngredientsData] = useModel(
    ProteinFormulationService,
    'getGlobalIngredientsData',
  );
  const showFileNotFoundModal = useFileNotFoundModal();

  const beforeUnloadListener = useCallback((event) => {
    event.preventDefault();
    return (event.returnValue = 'Are you sure you want to exit?');
  }, []);

  const onUnloadRemoveDocumentListener = () => {
    const openedWindows = JSON.parse(localStorage.getItem('openedNodesIds'));
    if (openedWindows && openedWindows.length) {
      const openedWindowsList = openedWindows.filter((win) => win != treeNodeId);
      localStorage.setItem('openedNodesIds', JSON.stringify(openedWindowsList));
    }
  };

  const isReadOnly = accessRightName !== ETreeNodeAccessRightNames.Modify;

  useEffect(() => {
    if (treeNodeId != -1) {
      saveDocumentToRecentlyOpenedList(treeNodeId);
    }

    async function getProteinFormulationData() {
      const tnId = !treeNodeId || treeNodeId === 'undefined' ? -1 : treeNodeId;
      const ingsResult: Promise<GlobalIngredientsDTO> = getGlobalIngredientsData();
      ingsResult.then((result) => {
        setGlobalIngredientsData(result);
      });

      const result: Promise<PFDocDataDTO> = getProteinFormulationDocData(tnId);
      const treeService = new TreeService();
      const treeNodeRequest = treeService.getTreeNodeById(treeNodeId);
      treeNodeRequest.then((node) => {
        setTreeNode(node);
      });
      result
        .then((data) => {
          setPfIngTypes([...data.dropDownsData.pfIngTypes]);
          setLiquidClasses([...data.dropDownsData.liquidClasses]);
          convertAndSetAttachmentData(data.proteinFormulation);
          setProteinFormulation({
            ...data.proteinFormulation,
          } as PFDocProteinFormulationDTO);
          setPFName(data.proteinFormulation.pfName);
          setDatePrepared(data.proteinFormulation.datePrepared);
          saveUnChangedData(data.proteinFormulation);
          setLoading({ show: false, text: 'Loading' });
          setAccessRightName(data.proteinFormulation.accessRightName);
        })
        .catch((error) => {
          setLoading((prev) => ({ ...prev, show: false }));
          if (error.StatusCode == 404) {
            showFileNotFoundModal();
          } else {
            throw error;
          }
        });
    }
    getProteinFormulationData();
  }, []);

  useEffect(() => {
    if (
      datePrepared != proteinFormulation.datePrepared &&
      proteinFormulation.pfState === PFStateDTO.UnChanged
    ) {
      proteinFormulation.pfState = PFStateDTO.Modified;
      setSaveDisabled(false);
    }
  }, [datePrepared]);

  useEffect(() => {
    if (pfName != '' && pfName != proteinFormulation.pfName) {
      proteinFormulation.pfState = PFStateDTO.Renamed;
      setSaveDisabled(false);
    }
    document.title = pfName;
  }, [pfName]);

  useEffect(() => {
    const openedWindows = JSON.parse(localStorage.getItem('openedNodesIds'));
    const openedWindowsList = !openedWindows ? [] : openedWindows;
    if (!openedWindows || !openedWindowsList.find((win) => win == treeNodeId)) {
      openedWindowsList.push(treeNodeId);
      localStorage.setItem('openedNodesIds', JSON.stringify(openedWindowsList));
    }

    if (
      !isNullOrUndefined(proteinFormulation.pfState) &&
      proteinFormulation.pfState !== PFStateDTO.UnChanged
    ) {
      setSaveDisabled(
        (proteinFormulation.pfState as PFStateDTO) === PFStateDTO.UnChanged,
      );
    }
    window.addEventListener('beforeunload', onUnloadRemoveDocumentListener);
    return () => {
      window.removeEventListener('beforeunload', beforeUnloadListener);
      window.removeEventListener('beforeunload', onUnloadRemoveDocumentListener);
    };
  }, [proteinFormulation]);
  useEffect(() => {
    if (!saveDisabled) {
      window.addEventListener('beforeunload', beforeUnloadListener);
    } else {
      window.removeEventListener('beforeunload', beforeUnloadListener);
    }
  }, [saveDisabled]);

  const handleUserKeyPress = useCallback(
    (e: KeyboardEvent) => {
      const ctrlKey = e.ctrlKey || e.metaKey;

      if (ctrlKey && e.key === KeyboardKeys.S) {
        e.preventDefault();
        if (!saveDisabled) {
          saveHandler();
        }
      }
    },
    [saveHandler, saveDisabled],
  );

  useEffect(() => {
    const abortController = new AbortController();
    const signal = abortController.signal;

    window.addEventListener('keydown', handleUserKeyPress, { signal });

    return () => {
      abortController.abort();
    };
  }, [handleUserKeyPress]);

  async function saveDocumentToRecentlyOpenedList(nodeId) {
    const recentDocumentService = new RecentDocumentService();
    await recentDocumentService.saveDocumentToRecents(nodeId);
  }

  function onChangeHandler(e, key) {
    proteinFormulation[key] = e.target.value;
    if (proteinFormulation.pfState === PFStateDTO.UnChanged) {
      proteinFormulation.pfState = PFStateDTO.Modified;
    }

    setProteinFormulation({
      ...proteinFormulation,
    } as PFDocProteinFormulationDTO);
  }

  function addAttachmentHandler() {
    if (!isNullOrUndefined(inputFileRef)) {
      inputFileRef.current.click();
    }
  }

  function attachmentChangedHandler(e) {
    const file = e.target.files[0];
    if (
      !isNullOrUndefined(
        proteinFormulation.proteinFormulationAttachments.find(
          (at) => at.name == file.name,
        ),
      )
    ) {
      showSnackBar(`A file with name '${file.name}' has already been added.`);
    } else {
      convertAttachmentToStringData(file);
      setSaveDisabled(false);
    }
    e.target.value = '';
  }

  function removeAttachmentHandler(name) {
    const idx = proteinFormulation.proteinFormulationAttachments.findIndex(
      (at) => at.name == name,
    );
    const att = proteinFormulation.proteinFormulationAttachments[idx];
    if (att.attachmentState != PFStateDTO.New) {
      const deletedAttachment = new PFDocAttachmentDTO();
      deletedAttachment.name = att.name;
      deletedAttachment.proteinFormulationId = att.proteinFormulationId;
      deletedAttachment.attachmentState = PFStateDTO.Deleted;
      deletedPFAttachments.push(deletedAttachment);
    }
    proteinFormulation.proteinFormulationAttachments.splice(idx, 1);
    setProteinFormulation({
      ...proteinFormulation,
    } as PFDocProteinFormulationDTO);
    setSaveDisabled(false);
  }

  async function convertAttachmentToStringData(file) {
    const reader = new FileReader();
    const url = URL.createObjectURL(file);
    setLoading({ show: true, text: 'Loading Attachment' });
    reader.onload = function (e) {
      const result = e.target.result;
      const pfAttachment = new PFDocAttachmentDTO();
      pfAttachment.name = file.name;
      pfAttachment.data = result.toString().split(',')[1];
      pfAttachment.proteinFormulationId = proteinFormulation.id;
      pfAttachment.attachmentState = PFStateDTO.New;
      pfAttachment.link = url;
      proteinFormulation.proteinFormulationAttachments.push(pfAttachment);
      setLoading({ show: false, text: 'Loading Attachment' });
    };
    reader.readAsDataURL(file);
  }

  async function saveHandler() {
    if (!validatePFData()) {
      return;
    }
    try {
      setLoading({ show: true, text: 'Saving' });
      const saveData: PFDocSaveProteinFormulationDTO =
        new PFDocSaveProteinFormulationDTO();
      saveData.proteinFormulation = new PFDocProteinFormulationDTO();
      saveData.proteinFormulation.init(proteinFormulation);
      saveData.proteinFormulation.pfName = pfName.trim();
      const nowTime = new Date();
      saveData.proteinFormulation.datePrepared = new Date(
        datePrepared.getFullYear(),
        datePrepared.getMonth(),
        datePrepared.getDate(),
        nowTime.getHours(),
        nowTime.getMinutes(),
        nowTime.getSeconds(),
      );
      saveData.deletedPFIngs = deletedProteinIngredients;
      saveData.deletedStIngs = deletedStandardIngredients;
      saveData.deletedAttachments = deletedPFAttachments;
      const pfService: ProteinFormulationService = new ProteinFormulationService();
      const result: PFDocSaveResult = await pfService.saveProteinFormulation(saveData);
      if (result.error) {
        setLoading({ show: false, text: 'Saving' });
        setSaveDisabled(false);
        showAlertSnackBar(
          EAlertType.ERROR,
          ESnackBarPosition.TOP,
          `${result.errorMessage}`,
        );
      } else {
        const savedPF = result.data;
        savedPF.pfName = pfName;
        convertAndSetAttachmentData(savedPF);
        setProteinFormulation(savedPF);
        saveUnChangedData(savedPF);
        setLoading({ show: false, text: 'Saving' });
        deletedProteinIngredients = [];
        deletedStandardIngredients = [];
        deletedPFAttachments = [];
        setSaveDisabled(true);
        SetGlobalExpData(true); //update global data
        showSnackBar('Saved Successfuly!');
        explorerBroadcastChannel.postMessage({
          action: CHANNEL_ACTIONS.EXPLORER_RENAME_NODE,
          payload: { ...treeNode, name: pfName },
        });
        globalDataBroadcastChannel.postMessage({
          action: CHANNEL_ACTIONS.GLOBAL_DATA_UPDATE,
          payload: {
            updatedEntity: 'proteinFormulation',
          },
        });
      }
    } catch (err) {
      console.error(err);
      setLoading({ show: false, text: 'Saving' });
      setSaveDisabled(false);
      if (err.StatusCode === 404) {
        showFileNotFoundModal();
      } else {
        showSnackBar('Error while Saving Protein Formulation');
      }
    }
  }

  const updateProteinIngredientsHandler = useCallback(
    (
      pfIngs: PFDocProteinFormulationIngredientsDTO[],
      deletedIngs: PFDocProteinFormulationIngredientsDTO[] = null,
    ) => {
      setProteinFormulation(
        (prev) =>
          ({
            ...prev,
            proteinFormulationIngredient: pfIngs,
          } as PFDocProteinFormulationDTO),
      );
      if (!isNullOrUndefined(deletedIngs)) {
        deletedProteinIngredients = [...deletedProteinIngredients, ...deletedIngs];
      }
      setSaveDisabled(false);
    },
    [],
  );

  function updateOrDeleteStandardIngsHandler(
    deletedPfStIng: PFDocStandardIngredientsDTO,
  ) {
    if (!isNullOrUndefined(deletedPfStIng)) {
      deletedStandardIngredients.push(deletedPfStIng);
    }
    setSaveDisabled(false);
  }

  function convertAndSetAttachmentData(pfData: PFDocProteinFormulationDTO) {
    if (isNullOrUndefined(pfData.proteinFormulationAttachments)) {
      return;
    }

    const attachmentsArray = [];
    pfData.proteinFormulationAttachments.forEach((att) => {
      const b64Data = att.data;
      const ext = att.name.split('.').pop();
      const contentType = MIME_TYPES[ext.toLowerCase()];
      const byteCharacters = atob(b64Data);

      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);

      const file = new Blob([byteArray], { type: contentType });
      const url = URL.createObjectURL(file);
      attachmentsArray.push({
        ...att,
        link: url,
        attachmentState: PFStateDTO.UnChanged,
      });
    });
    pfData.proteinFormulationAttachments = attachmentsArray;
  }

  function handleDiscardChanges() {
    const clonedPfData: PFDocProteinFormulationDTO = cloneDeep({
      ...unChangedProteinFormulation,
    }) as PFDocProteinFormulationDTO;
    setProteinFormulation(clonedPfData);
    setPFName(clonedPfData.pfName);
    setDatePrepared(clonedPfData.datePrepared);
    convertAndSetAttachmentData({
      ...clonedPfData,
    } as PFDocProteinFormulationDTO);
    setSaveDisabled(true);
    deletedProteinIngredients = [];
    deletedStandardIngredients = [];
    deletedPFAttachments = [];
  }

  function saveUnChangedData(pfDocData: PFDocProteinFormulationDTO) {
    unChangedProteinFormulation = cloneDeep({
      ...pfDocData,
    }) as PFDocProteinFormulationDTO;
  }

  function validatePFData(): boolean {
    let isValid = true;
    if (pfName.trim() == '' || !pfName.trim()) {
      isValid = false;
      setPFError('Protein Formulation Name is a required field.');
    }
    for (let i = 0; i < proteinFormulation.proteinFormulationIngredient.length; i++) {
      const pfIngName = proteinFormulation.proteinFormulationIngredient[i].name;
      if (isNullOrUndefined(pfIngName) || pfIngName == '') {
        isValid = false;
        proteinFormulation.proteinFormulationIngredient[i].name = '';
        setPFError('Ingredient Name is a required field.');
      }
    }
    if (!isValid) {
      setShowError(true);
      setProteinFormulation({
        ...proteinFormulation,
      } as PFDocProteinFormulationDTO);
    }
    return isValid;
  }

  const saveDiscardButtons = useSaveDiscardButtons({
    enableButtons: !saveDisabled && !isReadOnly,
    onSave: saveHandler,
    onDiscard: handleDiscardChanges,
  });

  const setProteinName = (e) => {
    setPFName(e.target.value);
  };

  return (
    <>
      <div id="addprotein-container" className="h-[100%] w-[100%]">
        <LoadingModal showModal={loading.show} text={loading.text} />
        <Header isNewTheme={true} title={pfName} headerButtons={saveDiscardButtons} />
        <div
          id="addprotein-mainwrapper"
          className="w-[100%] h-[89.4%] flex flex-row items-center justify-center"
        >
          <div
            id="addprotein-contentwrapper"
            className="w-[97.6%] h-[98%] flex flex-row justify-between"
          >
            <div
              id="addprotein-leftsection"
              className="w-[75.46%] h-[100%] flex flex-col justify-between"
            >
              <div
                id="addprotein-upperinfo"
                className="w-[100%] h-[35.35%] flex flex-row justify-between"
              >
                <div
                  id="addprotein-infoleft"
                  className="w-[53.08%] h-[100%] flex flex-col justify-between"
                >
                  <div
                    id="addprotein-proteinname-row"
                    className="w-[100%] h-[13.3%] flex flex-row"
                  >
                    <div
                      id="addprotein-proteinname-label"
                      className="h-[100%] w-[16.1%] text-[0.923rem] text-[#1B1B1B] place-content-center"
                    >
                      Protein Name
                    </div>
                    <div
                      id="addprotein-proteinname-inputcontainer"
                      className="h-[100%] w-[83.9%] place-content-center"
                    >
                      <input
                        id="addprotein-proteinname-input"
                        className="w-[100%] h-[92.1%] text-[1.077rem] text-[#707070]"
                        type="text"
                        value={pfName}
                        onChange={setProteinName}
                        readOnly={isReadOnly}
                      />
                    </div>
                  </div>
                  <div
                    id="addprotein-class-row"
                    className="w-[100%] h-[13.3%] flex flex-row"
                  >
                    <div
                      id="addprotein-class-label"
                      className="h-[100%] w-[16.1%] text-[0.923rem] text-[#1B1B1B] place-content-center"
                    >
                      Class
                    </div>
                    <div
                      id="addprotein-class-inputcontainer"
                      className="h-[100%] w-[83.9%] place-content-center"
                    >
                      <input
                        id="addprotein-class-input"
                        placeholder="Type here"
                        className="w-[100%] h-[92.1%] text-[1.077rem] text-[#707070]"
                        type="text"
                        onChange={(e) => onChangeHandler(e, 'pclass')}
                        value={proteinFormulation.pclass ? proteinFormulation.pclass : ''}
                        readOnly={isReadOnly}
                      />
                    </div>
                  </div>
                  <div
                    id="addprotein-idblock"
                    className="w-[100%] h-[65.3%] flex flex-row justify-between"
                  >
                    <div
                      id="addprotein-idleft"
                      className="w-[48.51%] h-[100%] flex flex-col justify-between"
                    >
                      <div
                        id="addprotein-id-row"
                        className="w-[100%] h-[20.32%] flex flex-row"
                      >
                        <div
                          id="addprotein-id-label"
                          className="w-[33.2%] text-[0.923rem] text-[#1B1B1B] place-content-center"
                        >
                          ID
                        </div>
                        <div
                          id="addprotein-id-value"
                          className="w-[66.8%] text-[#414141] text-[1.077rem] place-content-center"
                        >
                          {proteinFormulation.id}
                        </div>
                      </div>
                      <div
                        id="addprotein-lotid-row"
                        className="w-[100%] h-[20.32%] flex flex-row"
                      >
                        <div
                          id="addprotein-lotid-label"
                          className="w-[33.2%] h-[100%] text-[0.923rem] text-[#1B1B1B] place-content-center"
                        >
                          Protein Lot ID
                        </div>
                        <div
                          id="addprotein-lotid-inputcontainer"
                          className="w-[66.8%] place-content-center"
                        >
                          <input
                            id="addprotein-lotid-input"
                            placeholder="Type here"
                            className="w-[100%] h-[92.4%] text-[1.077rem] text-[#707070]"
                            type="text"
                            onChange={(e) => onChangeHandler(e, 'proteinLotId')}
                            value={
                              proteinFormulation.proteinLotId
                                ? proteinFormulation.proteinLotId
                                : ''
                            }
                            readOnly={isReadOnly}
                          />
                        </div>
                      </div>
                      <div
                        id="addprotein-date-row"
                        className="w-[100%] h-[20.32%] flex flex-row"
                      >
                        <div
                          id="addprotein-date-label"
                          className="w-[33.2%] text-[0.923rem] text-[#1B1B1B] h-[100%] place-content-center"
                        >
                          Date Prepared
                        </div>
                        <div
                          id="addprotein-date-pickercontainer"
                          className="w-[66.8%] h-[92.4%]"
                        >
                          <RMDatePicker
                            date={datePrepared}
                            onChange={setDatePrepared}
                            className="h-[100%] w-[100%]"
                            isReadOnly={isReadOnly}
                          />
                        </div>
                      </div>
                      <div
                        id="addprotein-liquidclass-row"
                        className="w-[100%] h-[20.32%] flex flex-row"
                      >
                        <div
                          id="addprotein-liquidclass-label"
                          className="w-[33.2%] h-[100%] text-[0.923rem] place-content-center"
                        >
                          Liquid Class
                        </div>
                        <div
                          id="addprotein-liquidclass-selectcontainer"
                          className="w-[66.8%]"
                        >
                          <select
                            id="addprotein-liquidclass-select"
                            name="liquidClass"
                            onChange={(e) => onChangeHandler(e, 'liquidClassId')}
                            value={
                              proteinFormulation.liquidClassId
                                ? proteinFormulation.liquidClassId
                                : ''
                            }
                            className="w-[100%] h-[92.4%]"
                            disabled={isReadOnly}
                          >
                            {liquidClasses.map((val) => (
                              <option
                                id={`addprotein-liquidclass-option-${val.name}`}
                                key={val.id}
                                value={val.id}
                              >
                                {val.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                    <div
                      id="addprotein-idright"
                      className="w-[48.51%] h-[100%] flex flex-col justify-between"
                    >
                      <div
                        id="addprotein-volume-row"
                        className="w-[100%] h-[20.32%] flex flex-row"
                      >
                        <div
                          id="addprotein-volume-label"
                          className="text-[0.923rem] text-[#1B1B1B] w-[49.03%] h-[100%] place-content-center"
                        >
                          Volume
                        </div>
                        <div
                          id="addprotein-volume-inputcontainer"
                          className="flex flex-row h-[92.4%%] w-[50.96%] border border-solid border-[#999999] rounded-[2px] place-content-center  focus-within:outline focus-within:outline-[1px]"
                        >
                          <RMInput
                            placeHolder="Type Here"
                            className="w-[73.2%] h-[100%] text-[1.077rem] border-0 bg-[#FFFFFF] focus:outline-none"
                            value={proteinFormulation.volume}
                            onChange={(e) => onChangeHandler(e, 'volume')}
                            allowDecimals
                            readonly={isReadOnly}
                          />
                          <div
                            id="addprotein-volume-unit"
                            className="w-[26.8%] h-[100%] text-[1.077rem] text-[#414141] bg-[#FAFAFA] text-center place-content-center"
                          >
                            uL
                          </div>
                        </div>
                      </div>
                      <div
                        id="addprotein-temp-row"
                        className="w-[100%] h-[20.32%] flex flex-row"
                      >
                        <div
                          id="addprotein-temp-label"
                          className="text-[0.923rem] w-[49.03%] h-[100%] place-content-center"
                        >
                          Storage Temp.
                        </div>
                        <div
                          id="addprotein-temp-inputcontainer"
                          className="flex flex-row h-[92.4%%] w-[50.96%] border border-solid border-[#767676] rounded-[2px] place-content-center focus-within:outline focus-within:outline-[1px]"
                        >
                          <RMInput
                            placeHolder="Type Here"
                            className="w-[73.2%] h-[100%] text-[1.077rem] border-0 bg-[#FFFFFF] focus:outline-none"
                            value={
                              proteinFormulation.storageTemperature
                                ? proteinFormulation.storageTemperature
                                : ''
                            }
                            onChange={(e) => onChangeHandler(e, 'storageTemperature')}
                            allowNegatives
                            readonly={isReadOnly}
                          />
                          <div
                            id="addprotein-temp-unit"
                            className="w-[26.8%] h-[100%] text-[1.077rem] text-[#414141] text-center bg-[#FAFAFA] place-content-center"
                          >
                            °C
                          </div>
                        </div>
                      </div>
                      <div
                        id="addprotein-extinction-row"
                        className="w-[100%] h-[20.32%] flex flex-row"
                      >
                        <div
                          id="addprotein-extinction-label"
                          className="text-[0.923rem] w-[49.03%] h-[100%] place-content-center"
                        >
                          Extinction Coefficient
                        </div>
                        <div
                          id="addprotein-extinction-inputcontainer"
                          className="h-[100%] w-[50.96%] place-content-center"
                        >
                          <input
                            id="addprotein-extinction-input"
                            placeholder="Type Here"
                            className="w-[100%] h-[92.4%] text-[1.077rem] text-[#707070]"
                            type="text"
                            onChange={(e) => onChangeHandler(e, 'extinctionCoefficient')}
                            value={
                              proteinFormulation.extinctionCoefficient
                                ? proteinFormulation.extinctionCoefficient
                                : ''
                            }
                            readOnly={isReadOnly}
                          />
                        </div>
                      </div>
                      <div
                        id="addprotein-notebook-row"
                        className="w-[100%] h-[20.32%] flex flex-row"
                      >
                        <div
                          id="addprotein-notebook-label"
                          className="w-[49.03%] text-[0.923rem] h-[100%] place-content-center"
                        >
                          Notebook Page#
                        </div>
                        <div
                          id="addprotein-notebook-inputcontainer"
                          className="h-[100%] w-[50.96%] place-content-center"
                        >
                          <input
                            id="addprotein-notebook-input"
                            placeholder="Type Here"
                            className="w-[100%] h-[92.4%] text-[1.077rem] text-[#707070]"
                            type="text"
                            onChange={(e) => onChangeHandler(e, 'notebookPageNo')}
                            value={
                              proteinFormulation.notebookPageNo
                                ? proteinFormulation.notebookPageNo
                                : ''
                            }
                            readOnly={isReadOnly}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  id="addprotein-inforight"
                  className="w-[45.33%] h-[100%] flex flex-col justify-between"
                >
                  <div
                    id="addprotein-notes-row"
                    className="w-[100%] h-[80.6%] flex flex-row"
                  >
                    <div
                      id="addprotein-notes-label"
                      className="w-[16.67%] h-[16.45%] place-content-center text-[0.923rem] text-[#1B1B1B]"
                    >
                      Notes
                    </div>
                    <div
                      id="addprotein-notes-textarea-container"
                      className="w-[83.5%] h-[100%]"
                    >
                      <textarea
                        id="addprotein-notes-textarea"
                        placeholder="Type here"
                        className="w-[100%] h-[100%] text-[0.923rem] selection:bg-blue-500 selection:text-white"
                        style={{ resize: 'none' }}
                        rows={4}
                        onChange={(e) => onChangeHandler(e, 'notes')}
                        value={proteinFormulation.notes ? proteinFormulation.notes : ''}
                        readOnly={isReadOnly}
                      />
                    </div>
                  </div>
                  <div
                    id="addprotein-attachment-row"
                    className="w-[100%] h-[15.31%] flex flex-row"
                  >
                    <div
                      id="addprotein-attachment-label"
                      className="w-[16.67%] h-[100%] text-[0.923rem] text-[#1B1B1B] place-content-center"
                    >
                      Attachment
                    </div>
                    <div
                      id="addprotein-attachment-buttoncontainer"
                      className="w-[16%] h-[100%] content-center"
                    >
                      <RMCustomButton
                        onClick={() => {
                          addAttachmentHandler();
                        }}
                        isDisabled={isReadOnly}
                        title={'BROWSE'}
                        titleClass="text-[#313131] text-[1.077rem] font-bold"
                        titleTip={'Upload Attachment'}
                        contentContainerClass={'w-[100%] h-[100%] text-[#FFFFFF] '}
                        mainContainerClass={
                          'w-[100%] h-[80%] border border-solid border-[#B4B4B4] rounded-[4px]'
                        }
                      />
                      <input
                        id="addprotein-attachment-inputfile"
                        type="file"
                        ref={inputFileRef}
                        style={{ display: 'none' }}
                        onInput={attachmentChangedHandler}
                      />
                    </div>
                    <div
                      id="addprotein-attachment-list"
                      className="w-[67%] h-[100%] pl-[2%] text-[1.077rem] text-[#313131] place-content-center"
                    >
                      {proteinFormulation.proteinFormulationAttachments?.length > 0 && (
                        <div
                          id="addprotein-attachment-tooltip"
                          className="attachment-tooltip w-[100%] h-[100%]"
                        >
                          <div
                            id="addprotein-attachment-names"
                            className="w-[100%] h-[100%] truncate content-center"
                          >
                            {proteinFormulation.proteinFormulationAttachments
                              .map((attachment) => attachment.name)
                              .join(', ')}
                          </div>
                          <div
                            id="addprotein-attachment-tooltipcontent"
                            className="attachment-tooltip-content"
                          >
                            {proteinFormulation.proteinFormulationAttachments.map(
                              (att, i) => {
                                return (
                                  <div
                                    id={`addprotein-attachment-item-${i}`}
                                    key={`pfpfa${i}`}
                                    style={{ display: 'flex', alignItems: 'baseline' }}
                                  >
                                    <div style={{ flex: '90%' }}>
                                      {' '}
                                      {att && (
                                        <a
                                          id={`addprotein-attachment-link-${i}`}
                                          className="p-2 flex"
                                          download={att.name}
                                          href={att.link}
                                        >
                                          {att.name}
                                        </a>
                                      )}{' '}
                                    </div>
                                    <div>
                                      {' '}
                                      <RMCustomButton
                                        onClick={() => {
                                          removeAttachmentHandler(att.name);
                                        }}
                                        isDisabled={isReadOnly}
                                        titleTip={'Delete'}
                                        iconProps={{
                                          name: 'Delete',
                                          fill: 'white',
                                          ...iconSize,
                                        }}
                                        contentContainerStyle={{
                                          ...actionButtonStyle,
                                          backgroundColor: actionButtonsEnable.delete,
                                        }}
                                        mainContainerClass="place-content-start"
                                      />{' '}
                                    </div>
                                  </div>
                                );
                              },
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div
                id="addprotein-proteiningredients-section"
                className="w-[100%] h-[63.48%] flex flex-col justify-between"
              >
                <ProteinIngredients
                  proteinFormulationIngredients={
                    proteinFormulation.proteinFormulationIngredient
                  }
                  pfIngTypes={pfIngTypes}
                  setIngTypes={setPfIngTypes}
                  updatePfIngs={updateProteinIngredientsHandler}
                  isReadOnly={isReadOnly}
                />
              </div>
            </div>
            <div id="addprotein-standardingredients-section" className="w-[24%] h-[100%]">
              <StandardIngredients
                proteinFormulation={proteinFormulation}
                updateDeleteStIngs={updateOrDeleteStandardIngsHandler}
                globalIngredients={globalIngredientsData}
                isReadOnly={isReadOnly}
              />
            </div>
          </div>
        </div>
      </div>
      <RMModal
        id="addprotein-errormodal"
        showModal={showError}
        setShowModal={setShowError}
        onOk={() => setShowError(false)}
        body={pfErrors}
        heading="Error"
        okButtonText="OK"
        isOkOnly
        titleClr="#DB0000"
      />
    </>
  );
};

export default AddProtein;
